import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { SiteFooter } from '../components/SiteFooter';
import { PageGrid } from '../components/PageGrid';
import { DocsSidebar } from '../components/docs/DocsSidebar';
import { ShareSidebar } from '../components/article/ShareSidebar';
import { ArticleContent, extractHeadings } from '../components/article/ArticleBlockRenderer';
import { SitePageReveal } from '../components/SiteFadeIn';
import { useCachedPublishedArticles } from '../hooks/useCachedPublishedArticles';
import { panelMainClass } from '../lib/contentListPageLayout';
import { resolveContentListPageRevealPhase } from '../lib/contentListPageReveal';
import type { ArticleRow } from '../../../shared/schemas/article';
import type { DocNavItem, DocHeading } from '../components/docs/DocsSidebar';

const SECTION_ORDER: Record<string, number> = {
  'getting-started': 0,
  'requirements': 1,
  'questions': 2,
  'answers': 3,
  'summary': 4,
  'exports': 5,
  'monitoring': 6,
};

function sortArticlesBySectionOrder(articles: ArticleRow[]): ArticleRow[] {
  return [...articles].sort((a, b) => {
    const tagA = a.tags?.find((t) => t in SECTION_ORDER);
    const tagB = b.tags?.find((t) => t in SECTION_ORDER);
    const orderA = tagA ? (SECTION_ORDER[tagA] ?? 99) : 99;
    const orderB = tagB ? (SECTION_ORDER[tagB] ?? 99) : 99;
    return orderA - orderB;
  });
}

function useActiveHeading(headings: DocHeading[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (headings.length === 0) {
      return;
    }

    observerRef.current?.disconnect();

    const callback: IntersectionObserverCallback = (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visible.length > 0) {
        setActiveId(visible[0].target.id);
      }
    };

    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    });

    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [headings]);

  return headings.length === 0 ? null : activeId;
}

export function DocsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const {
    data: rawArticles,
    loading,
    error,
  } = useCachedPublishedArticles('/api/articles?type=docs');

  const articles = useMemo(
    () => (rawArticles ? sortArticlesBySectionOrder(rawArticles) : []),
    [rawArticles],
  );

  const activeArticle = useMemo(() => {
    if (articles.length === 0) return null;
    if (slug) {
      return articles.find((article) => article.slug === slug) ?? articles[0];
    }
    return articles[0];
  }, [articles, slug]);

  useEffect(() => {
    if (loading || articles.length === 0 || slug) return;
    navigate(`/docs/${articles[0].slug}`, { replace: true });
  }, [articles, loading, navigate, slug]);

  const pages: DocNavItem[] = useMemo(
    () => articles.map((article) => ({ title: article.title, slug: article.slug })),
    [articles],
  );

  const headings: DocHeading[] = useMemo(() => {
    if (!activeArticle?.content) return [];
    return extractHeadings(activeArticle.content);
  }, [activeArticle?.content]);

  const activeHeadingId = useActiveHeading(headings);
  const contentReady = resolveContentListPageRevealPhase(!loading && articles.length > 0) === 'ready';

  const handleNavigate = useCallback((targetSlug: string) => {
    navigate(`/docs/${targetSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const articleUrl = activeArticle
    ? `${window.location.origin}/docs/${activeArticle.slug}`
    : window.location.href;

  return (
    <div className="min-h-screen bg-surface-base text-text-primary antialiased">
      <TopNav />

      <PageGrid>
        <SitePageReveal ready={contentReady}>
          <div className="content-list-panels">
            <div className="content-list-panel-left hidden shrink-0 self-stretch lg:block">
              <nav className="sticky top-20 pt-30 w-article-sidebar max-h-[calc(100vh-var(--space-20))] overflow-y-auto pb-10 hide-scrollbar">
                <DocsSidebar
                  pages={pages}
                  activeSlug={activeArticle?.slug ?? null}
                  headings={headings}
                  activeHeadingId={activeHeadingId}
                  onNavigate={handleNavigate}
                />
              </nav>
            </div>

            <main className={`${panelMainClass(true, true)} min-h-[80vh] pb-40`}>
              {error ? (
                <p className="text-body text-status-error">{error}</p>
              ) : (
                <>
                  <div className="flex flex-col gap-4 pb-10">
                    <h1 className="text-h2 text-text-primary">{activeArticle?.title ?? 'Docs'}</h1>
                    {activeArticle?.excerpt && (
                      <p className="text-body text-text-tertiary">{activeArticle.excerpt}</p>
                    )}
                  </div>
                  {activeArticle && <ArticleContent content={activeArticle.content} />}
                </>
              )}
            </main>

            <div className="content-list-panel-right hidden shrink-0 self-stretch lg:block">
              <div className="sticky top-20 pt-30">
                <ShareSidebar articleUrl={articleUrl} />
              </div>
            </div>
          </div>
        </SitePageReveal>
      </PageGrid>
      <SiteFooter ready={contentReady} />
    </div>
  );
}
